// PWA 앱 아이콘 배지 관리 유틸리티
// 스마트폰의 앱 아이콘에 배지 숫자를 표시하는 기능

/**
 * PWA Badge API 지원 여부 확인
 */
export const isAppBadgeSupported = (): boolean => {
  return 'setAppBadge' in navigator && 'clearAppBadge' in navigator && 
         typeof navigator.setAppBadge === 'function' && 
         typeof navigator.clearAppBadge === 'function';
};

/**
 * 앱 아이콘 배지 설정
 * @param count 배지에 표시할 숫자 (0이면 배지 제거)
 */
export const setAppBadge = async (count: number): Promise<boolean> => {
  try {
    if (!isAppBadgeSupported()) {
      console.warn('⚠️ [앱 배지] PWA Badge API가 지원되지 않습니다');
      return false;
    }

    if (count > 0) {
      await (navigator as any).setAppBadge(count);
      console.log('✅ [앱 배지] 앱 아이콘 배지 설정 성공:', count);
    } else {
      await (navigator as any).clearAppBadge();
      console.log('✅ [앱 배지] 앱 아이콘 배지 제거 성공');
    }
    
    return true;
  } catch (error) {
    console.error('❌ [앱 배지] 앱 아이콘 배지 설정 실패:', error);
    return false;
  }
};

/**
 * Service Worker를 통해 앱 아이콘 배지 업데이트
 * @param count 배지에 표시할 숫자
 */
export const updateAppBadgeViaServiceWorker = async (count: number): Promise<boolean> => {
  try {
    if (!('serviceWorker' in navigator)) {
      console.warn('⚠️ [앱 배지] Service Worker가 지원되지 않습니다');
      return false;
    }

    const registration = await navigator.serviceWorker.ready;
    if (registration.active) {
      registration.active.postMessage({
        type: 'UPDATE_APP_BADGE',
        count: count,
        timestamp: Date.now()
      });
      console.log('📱 [앱 배지] Service Worker를 통해 배지 업데이트 요청:', count);
      return true;
    } else {
      console.warn('⚠️ [앱 배지] 활성 Service Worker가 없습니다');
      return false;
    }
  } catch (error) {
    console.error('❌ [앱 배지] Service Worker를 통한 배지 업데이트 실패:', error);
    return false;
  }
};

/**
 * PWA 설치 상태 확인
 */
export const checkPWAInstallation = (): boolean => {
  // PWA가 설치되었는지 확인하는 여러 방법
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const isInApp = (window.navigator as any).standalone === true;
  const hasManifest = document.querySelector('link[rel="manifest"]') !== null;
  
  const isPWAInstalled = isStandalone || isInApp;
  
  console.log('📱 [PWA 설치 상태]', {
    isStandalone,
    isInApp,
    hasManifest,
    isPWAInstalled,
    displayMode: window.matchMedia('(display-mode: standalone)').media
  });
  
  return isPWAInstalled;
};


/**
 * 앱 아이콘 배지 업데이트 (간소화된 버전)
 * @param count 배지에 표시할 숫자
 */
export const updateAppBadge = async (count: number): Promise<boolean> => {
  console.log('🔄 [앱 배지] 배지 업데이트 시작:', count);
  
  // 1. 직접 PWA Badge API 시도
  const directResult = await setAppBadge(count);
  if (directResult) {
    console.log('✅ [앱 배지] PWA Badge API 성공');
    return true;
  }
  
  // 2. Service Worker를 통한 업데이트 시도
  const swResult = await updateAppBadgeViaServiceWorker(count);
  if (swResult) {
    console.log('✅ [앱 배지] Service Worker 업데이트 성공');
    return true;
  }
  
  console.warn('⚠️ [앱 배지] 앱 아이콘 배지 업데이트 실패 - 하단 네비게이션 배지는 정상 작동');
  return false;
};

/**
 * 브라우저별 앱 배지 지원 정보
 */
export const getAppBadgeSupportInfo = () => {
  const userAgent = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  const isAndroid = /Android/.test(userAgent);
  const isChrome = /Chrome/.test(userAgent) && !/Edge/.test(userAgent);
  const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
  const isFirefox = /Firefox/.test(userAgent);
  const isEdge = /Edge/.test(userAgent);
  
  // Chrome 버전 추출
  const chromeMatch = userAgent.match(/Chrome\/(\d+)/);
  const chromeVersion = chromeMatch ? parseInt(chromeMatch[1]) : 0;
  
  // Android Chrome 버전별 지원 확인
  const isAndroidChromeSupported = isAndroid && isChrome && chromeVersion >= 108;
  
  const supportInfo = {
    isSupported: isAppBadgeSupported(),
    platform: isIOS ? 'iOS' : isAndroid ? 'Android' : 'Desktop',
    browser: isChrome ? 'Chrome' : isSafari ? 'Safari' : isFirefox ? 'Firefox' : isEdge ? 'Edge' : 'Unknown',
    browserVersion: isChrome ? chromeVersion : 0,
    hasServiceWorker: 'serviceWorker' in navigator,
    hasNotification: 'Notification' in window,
    hasPushManager: 'PushManager' in window,
    isSecureContext: window.isSecureContext,
    isAndroidChromeSupported: isAndroidChromeSupported,
    userAgent: userAgent
  };
  
  console.log('📱 [앱 배지] 지원 정보:', supportInfo);
  
  // 안드로이드 Chrome 지원 상태 상세 로그
  if (isAndroid && isChrome) {
    if (chromeVersion >= 108) {
      console.log('✅ [앱 배지] Android Chrome 지원됨 - 버전:', chromeVersion);
    } else {
      console.warn('⚠️ [앱 배지] Android Chrome 버전 부족 - 현재:', chromeVersion, '필요: 108+');
    }
  }
  
  return supportInfo;
};

/**
 * 앱 배지 기능 초기화 및 지원 여부 확인
 */
export const initializeAppBadge = async (): Promise<boolean> => {
  const supportInfo = getAppBadgeSupportInfo();
  
  if (!supportInfo.isSupported) {
    console.warn('⚠️ [앱 배지] PWA Badge API가 지원되지 않습니다:', supportInfo);
    
    // 브라우저별 안내 메시지
    if (supportInfo.platform === 'iOS' && supportInfo.browser === 'Safari') {
      console.log('🍎 [앱 배지] iOS Safari: iOS 16.4+ 및 Safari 16.4+ 필요');
    } else if (supportInfo.platform === 'Android' && supportInfo.browser === 'Chrome') {
      console.log('🤖 [앱 배지] Android Chrome: Chrome 108+ 필요');
      console.log('🤖 [앱 배지] 현재 Chrome 버전:', supportInfo.browserVersion);
      console.log('🤖 [앱 배지] 지원 여부:', supportInfo.isAndroidChromeSupported);
    } else if (supportInfo.platform === 'Desktop') {
      console.log('💻 [앱 배지] 데스크탑: Chrome 108+, Edge 108+ 필요');
    }
    
    return false;
  }
  
  console.log('✅ [앱 배지] PWA Badge API 지원됨:', supportInfo);
  
  // 초기 배지 제거
  await setAppBadge(0);
  
  return true;
};


/**
 * 앱 배지 디버깅 정보 출력 (간소화된 버전)
 */
export const debugAppBadge = (): void => {
  const supportInfo = getAppBadgeSupportInfo();
  
  console.log('🔍 [앱 배지] 지원 정보:');
  console.log('  - 플랫폼:', supportInfo.platform);
  console.log('  - 브라우저:', supportInfo.browser);
  console.log('  - PWA Badge API 지원:', supportInfo.isSupported);
  console.log('  - PWA 설치됨:', checkPWAInstallation());
  
  if (supportInfo.isSupported) {
    console.log('✅ [앱 배지] PWA Badge API가 지원됩니다');
  } else {
    console.log('⚠️ [앱 배지] PWA Badge API가 지원되지 않습니다 - 하단 네비게이션 배지를 활용하세요');
  }
};

/**
 * 앱 배지 지원 상태에 따른 사용자 안내 메시지
 */
export const getAppBadgeSupportMessage = (): string => {
  const supportInfo = getAppBadgeSupportInfo();
  
  if (supportInfo.isSupported) {
    return `✅ 앱 아이콘 배지가 지원됩니다 (${supportInfo.platform} ${supportInfo.browser} ${supportInfo.browserVersion})`;
  }
  
  if (supportInfo.platform === 'iOS' && supportInfo.browser === 'Safari') {
    return '🍎 iOS Safari: iOS 16.4+ 및 Safari 16.4+에서 앱 아이콘 배지를 지원합니다';
  } else if (supportInfo.platform === 'Android' && supportInfo.browser === 'Chrome') {
    if (supportInfo.browserVersion > 0) {
      return `🤖 Android Chrome: 현재 버전 ${supportInfo.browserVersion}, Chrome 108+ 필요 (앱 아이콘 배지 지원)`;
    } else {
      return '🤖 Android Chrome: Chrome 108+에서 앱 아이콘 배지를 지원합니다';
    }
  } else if (supportInfo.platform === 'Desktop') {
    return '💻 데스크탑: Chrome 108+, Edge 108+에서 앱 아이콘 배지를 지원합니다';
  }
  
  return '⚠️ 현재 브라우저에서는 앱 아이콘 배지를 지원하지 않습니다';
};
